/**
 * Challenge Participant Service
 *
 * Service for managing challenge participants including
 * adding, updating, deleting, and listing participants.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { challengeService } from './challenge.service';
import { CrudService } from './crud.service';
import {
  validateChallengeParticipants,
  validateChallengeParticipantStatus,
  type ChallengeParticipantsInput,
  type ChallengeParticipantStatusInput,
} from '../validation/challenge.schemas';

import type { QueryParams } from '../types/api/query.types';
import type { ID } from '../types/common/id.types';
import type { ChallengeParticipantWithId } from '../types/domain/challenge.types';

// =============================================================================
// CHALLENGE PARTICIPANT SERVICE CLASS
// =============================================================================

/**
 * ChallengeParticipantService - Manages challenge participants
 * Handles adding and removing challenge participants
 *
 * Every successful write also invalidates `challengeService`'s cached detail
 * and lists: a roster change shows in both, but they are cached by that other
 * instance, so without it a refetch inside the TTL serves the pre-write
 * roster with no network request. A failed write leaves them alone.
 */
export class ChallengeParticipantService extends CrudService<ChallengeParticipantWithId> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/challenge-participant',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  /**
   * Get participants for a challenge
   */
  public async getParticipants(
    challengeId: ID,
    params?: QueryParams
  ): Promise<ChallengeParticipantWithId[]> {
    if (!challengeId) {
      throw new Error(
        'Challenge ID is required to fetch challenge participants.'
      );
    }

    try {
      return await this.withContext({
        template: '/challenge/{challenge}/challenge-participant',
        parameters: { challenge: challengeId },
      }).getAll(params);
    } catch (error) {
      console.error(
        `Failed to fetch participants for challenge ${challengeId}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to fetch challenge participants. Please try again.'
      );
    }
  }

  /**
   * Add user to challenge
   */
  public async addParticipant(
    challengeId: ID,
    data: ChallengeParticipantsInput
  ): Promise<ChallengeParticipantWithId[]> {
    if (!challengeId) {
      throw new Error('Challenge ID is required to add user.');
    }

    try {
      this.validateChallengeParticipantsData(data);

      // A ChallengeParticipantCollection: the synced roster under `data`.
      const response = await apiClient.post<{
        data: ChallengeParticipantWithId[];
      }>(`/challenge/${challengeId}/challenge-participant`, data);

      // Invalidate caches related to this challenge
      this.invalidateCache(['getAll'], challengeId);
      challengeService.invalidateChallengeCache(challengeId);

      return response.data.data;
    } catch (error) {
      console.error(
        `Failed to add user participant(s) to challenge ${challengeId}:`,
        error
      );

      rethrowTyped(
        error,
        'Unable to add participant(s) to challenge. Please try again.'
      );
    }
  }

  /**
   * Withdraw user from challenge
   *
   * Used to withdraw the authenticated user from a challenge
   */
  public async withdrawParticipant(
    challengeId: ID,
    challengeParticipantId: ID
  ): Promise<void> {
    if (!challengeId) {
      throw new Error('Challenge participant ID is required to withdraw user.');
    }

    try {
      await apiClient.delete(
        `/challenge-participant/${challengeParticipantId}/withdraw`
      );

      // Invalidate caches related to this challenge
      this.invalidateCache(['getAll'], challengeId);
      challengeService.invalidateChallengeCache(challengeId);
    } catch (error) {
      console.error(
        `Failed to withdraw participant ${challengeParticipantId} from challenge ${challengeId}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to withdraw participant from challenge. Please try again.'
      );
    }
  }

  /**
   * Update challenge participant status
   */
  public async updateParticipant(
    challengeParticipantId: ID,
    data: ChallengeParticipantStatusInput
  ): Promise<void> {
    if (!challengeParticipantId) {
      throw new Error(
        'Challenge participant ID is required to update participant status.'
      );
    }

    try {
      this.validateChallengeParticipantStatusData(data);

      await apiClient.patch<ChallengeParticipantStatusInput>(
        `/challenge-participant/${challengeParticipantId}`,
        data
      );

      // The participant id names no challenge, so every cached detail goes.
      challengeService.invalidateChallengeCache();
    } catch (error) {
      console.error(
        `Failed to update participant ${challengeParticipantId}:`,
        error
      );

      rethrowTyped(
        error,
        'Unable to update participant status. Please try again.'
      );
    }
  }

  /**
   * Resend participant invitation
   */
  public async resendInvitation(challengeParticipantId: ID): Promise<void> {
    if (!challengeParticipantId) {
      throw new Error(
        'Challenge participant ID is required to resend invitation.'
      );
    }

    try {
      await apiClient.post(
        `/challenge-participant/${challengeParticipantId}/resend`
      );

      // The participant id names no challenge, so every cached detail goes.
      challengeService.invalidateChallengeCache();
    } catch (error) {
      console.error(
        `Failed to resend invitation for participant ${challengeParticipantId}:`,
        error
      );
      rethrowTyped(error, 'Unable to resend invitation. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate challenge participants payload using Zod schema from challenge.schemas.ts.
   */
  protected validateChallengeParticipantsData(
    data: ChallengeParticipantsInput
  ): void {
    try {
      validateChallengeParticipants(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod(
          "Couldn't update the challenge participants",
          error,
          {
            originalData: data,
            details: { service: 'ChallengeParticipantService' },
          }
        );
      }

      throw new InvalidDataError(
        "Couldn't update the challenge participants: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'ChallengeParticipantService' },
        }
      );
    }
  }

  /**
   * Validate challenge participant status payload using Zod schema from challenge.schemas.ts.
   */
  protected validateChallengeParticipantStatusData(
    data: ChallengeParticipantStatusInput
  ): void {
    try {
      validateChallengeParticipantStatus(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod(
          "Couldn't update the participant status",
          error,
          {
            originalData: data,
            details: { service: 'ChallengeParticipantService' },
          }
        );
      }

      throw new InvalidDataError(
        "Couldn't update the participant status: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'ChallengeParticipantService' },
        }
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default challenge participant service instance.
 */
export const challengeParticipantService = new ChallengeParticipantService();
